import { lazy } from 'react';

// Atoms
export { default as AssetImage } from './atoms/AssetImage/AssetImage';
export type { AssetImageId, AssetImageProps } from './atoms/AssetImage/AssetImage';
export { default as Avatar } from './atoms/Avatar/Avatar';
export type { AvatarProps, AvatarSize } from './atoms/Avatar/Avatar';
export { default as BackButton } from './atoms/BackButton/BackButton';
export type { BackButtonColor } from './atoms/BackButton/BackButton';
export { default as Breadcrumbs } from './atoms/Breadcrumbs/Breadcrumbs';
export type { BreadcrumbItem, BreadcrumbsProps } from './atoms/Breadcrumbs/Breadcrumbs';
export { default as Button } from './atoms/Button/Button';
export type { ButtonShape, ButtonSize, ButtonType, ButtonVariant } from './atoms/Button/Button';
export { default as Card } from './atoms/Card/Card';
export type { CardProps } from './atoms/Card/Card';
export { default as CircularProgress } from './atoms/CircularProgress/CircularProgress';
export { default as Hr } from './atoms/Hr/Hr';
export { default as Icon } from './atoms/Icon/Icon';
export { default as Image } from './atoms/Image/Image';
export type { ImageProps } from './atoms/Image/Image';
export { default as InputCheckbox } from './atoms/InputCheckbox/InputCheckbox';
export { default as InputPassword } from './atoms/InputPassword/InputPassword';
export { default as InputRadio } from './atoms/InputRadio/InputRadio';
export { default as InputSearch } from './atoms/InputSearch/InputSearch';
export { default as InputText } from './atoms/InputText/InputText';
export { default as Loading } from './atoms/Loading/Loading';
export { default as LoadingDots } from './atoms/LoadingDots/LoadingDots';
export { default as ProgressMoon } from './atoms/ProgressMoon/ProgressMoon';
export { default as ProgressBar } from './atoms/ProgressBar/ProgressBar';
export { default as SelectCheckboxs } from './atoms/SelectCheckboxs/SelectCheckboxs';
export type { SelectCheckboxOption } from './atoms/SelectCheckboxs/SelectCheckboxs';
export { default as SelectCustomizable, utilsSelectCustomizable } from './atoms/SelectCustomizable/SelectCustomizable';
export type { SelectCustomizableOption, SelectCustomizableProps } from './atoms/SelectCustomizable/SelectCustomizable';
export { default as Skeleton } from './atoms/Skeleton/Skeleton';
export { default as Spinner } from './atoms/Spinner/Spinner';
export { default as Switch } from './atoms/Switch/Switch';
export { default as Tag } from './atoms/Tag/Tag';
export { default as TextArea } from './atoms/TextArea/TextArea';
export { default as TextHtml } from './atoms/TextHtml/TextHtml';
export type { TextHtmlProps } from './atoms/TextHtml/TextHtml';
export { default as Tooltip } from './atoms/Tooltip/Tooltip';

export type { IconBubbleBackground, IconBubbleProps, IconBubbleSize, IconBubbleVariant } from './molecules/IconBubble/types';
// Molecules
export { default as Accordion } from './molecules/Accordion/Accordion';
export type { AccordionProps, AccordionTab } from './molecules/Accordion/Accordion';
export { default as ActionButtonDropdownMenu } from './molecules/ActionButtonDropdownMenu/ActionButtonDropdownMenu';
export type {
  ActionButtonDropdownMenuIconColor,
  ActionButtonDropdownMenuOption,
  ActionButtonDropdownMenuProps,
} from './molecules/ActionButtonDropdownMenu/types';
export { default as ActionDropdown } from './molecules/ActionDropdown/ActionDropdown';
export type { ActionDropdownProps } from './molecules/ActionDropdown/types';
export { default as AuthProviderButton } from './molecules/AuthProviderButton/AuthProviderButton';
export type { AuthProvider } from './molecules/AuthProviderButton/AuthProviderButton';
export { default as CheckboxGroup } from './molecules/CheckboxGroup/CheckboxGroup';
export type { CheckboxGroupProps } from './molecules/CheckboxGroup/CheckboxGroup';
export { default as DatePicker } from './molecules/DatePicker/DatePicker/DatePicker';
export type { DatePickerProps } from './molecules/DatePicker/DatePicker/DatePicker';
export { default as DatePickerActions } from './molecules/DatePicker/DatePickerActions/DatePickerActions';
export type { DatePickerActionsProps } from './molecules/DatePicker/DatePickerActions/DatePickerActions';
export { default as DatePickerInline } from './molecules/DatePicker/DatePickerInline/DatePickerInline';
export type { DatePickerInlineProps } from './molecules/DatePicker/DatePickerInline/DatePickerInline';
export { default as ListComplexDropdown } from './molecules/ListComplexDropdown/ListComplexDropdown';
export type { ListComplexDropdownItem, ListComplexDropdownProps } from './molecules/ListComplexDropdown/types';
export { default as ListSimpleDropdown } from './molecules/ListSimpleDropdown/ListSimpleDropdown';
export type {
  ListSimpleDropdownIconColor,
  ListSimpleDropdownIconSize,
  ListSimpleDropdownItem,
  ListSimpleDropdownItems,
  ListSimpleDropdownProps,
} from './molecules/ListSimpleDropdown/types';
export { default as OptionsDropdownMenu } from './molecules/OptionsDropdownMenu/OptionsDropdownMenu';
export type { OptionsDropdownMenuOption, OptionsDropdownMenuOptions, OptionsDropdownMenuProps } from './molecules/OptionsDropdownMenu/types';
// Lazy: the recharts-based charts (heavy, drag d3-*). A dynamic import() keeps
// recharts out of the shared `ui` chunk — it only loads when a chart renders.
// Consumers still `import { PieChart } from 'ui'` and wrap usage in a
// <Suspense> boundary (see the docs / the router-level boundary in Routes.js).
export const PieChart = lazy(() => import('./molecules/charts/PieChart/PieChart'));
export const LineChart = lazy(() => import('./molecules/charts/LineChart/LineChart'));
export const AreaChart = lazy(() => import('./molecules/charts/AreaChart/AreaChart'));
export const BarChart = lazy(() => import('./molecules/charts/BarChart/BarChart'));
// Type-only re-exports (erased at compile time, so the charts stay lazy) — let
// consumers type their data/props without a forbidden deep import.
export type { PieChartDatum, PieChartProps, PieChartVariant } from './molecules/charts/PieChart/PieChart';
export type { LineChartDatum, LineChartProps, LineChartSeries } from './molecules/charts/LineChart/LineChart';
export type { AreaChartDatum, AreaChartProps, AreaChartSeries } from './molecules/charts/AreaChart/AreaChart';
export type { BarChartDatum, BarChartProps } from './molecules/charts/BarChart/BarChart';
export { default as Popover } from './molecules/Popover/Popover';
export type {
  PopoverHasPopup,
  PopoverModifiers,
  PopoverPlacement,
  PopoverProps,
  PopoverTriggerProps,
  UsePopoverParams,
  UsePopoverReturn,
} from './molecules/Popover/types';
export { default as FormField } from './molecules/FormField/FormField';
export { default as IconBubble } from './molecules/IconBubble/IconBubble';
export { default as Pagination } from './molecules/Pagination/Pagination';
export type { PaginationProps } from './molecules/Pagination/Pagination';
export { default as RadioGroup } from './molecules/RadioGroup/RadioGroup';
export type { RadioGroupProps } from './molecules/RadioGroup/RadioGroup';
export { default as SidebarTabs, getSidebarTabId } from './molecules/SidebarTabs/SidebarTabs';
export { default as SidebarTabPanel } from './molecules/SidebarTabs/SidebarTabPanel';
export type { SidebarTabItem } from './molecules/SidebarTabs/SidebarTabs';
export { default as Toast } from './molecules/Toast/Toast';
export type { ToastAppearance, ToastProps } from './molecules/Toast/types';
export { default as UploadArea } from './molecules/UploadArea/UploadArea';
export type { UploadAreaProps } from './molecules/UploadArea/UploadArea';

// Organisms
export { default as Dialog } from './organisms/dialogs/Dialog/Dialog';
export { default as DialogCarrouselImages } from './organisms/dialogs/DialogCarrouselImages/DialogCarrouselImages';
export { default as DialogConfirm } from './organisms/dialogs/DialogConfirm/DialogConfirm';
export { default as DialogCropImage } from './organisms/dialogs/DialogCropImage/DialogCropImage';
export { default as DialogImage } from './organisms/dialogs/DialogImage/DialogImage';
export { default as DialogRichTextInput } from './organisms/dialogs/DialogRichTextInput/DialogRichTextInput';
export { default as DialogShowExternalContent } from './organisms/dialogs/DialogShowExternalContent/DialogShowExternalContent';
export { default as DialogUploadFile } from './organisms/dialogs/DialogUploadFile/DialogUploadFile';
export { default as DialogWizard } from './organisms/dialogs/DialogWizard/DialogWizard';

export type { DialogSize } from './organisms/dialogs/Dialog/Dialog';
export type { DialogConfirmProps } from './organisms/dialogs/DialogConfirm/DialogConfirm';
export type { DialogRichTextInputProps } from './organisms/dialogs/DialogRichTextInput/DialogRichTextInput';
export type { DialogUploadFileProps } from './organisms/dialogs/DialogUploadFile/DialogUploadFile';
export type { DialogWizardProps } from './organisms/dialogs/DialogWizard/DialogWizard';

// Types
export type { IconName } from './icons/types';

// Hooks
export { default as useMenuNavigation } from './hooks/useMenuNavigation/useMenuNavigation';
export type {
  MenuItemProps,
  MenuOrientation,
  MenuProps,
  UseMenuNavigationParams,
  UseMenuNavigationReturn,
} from './hooks/useMenuNavigation/types';
export { usePopover } from './molecules/Popover/usePopover';
